import React, { useEffect, useRef, useState } from "react";
import "./StrippingMultivariableTrendChart.css";

const DURATIONS = {
  "1min": 60,
  "30min": 1800,
  "1hr": 3600,
  "5hr": 18000,
  "1day": 86400,
  "30days": 2592000
};

function StrippingMultivariableTrendChart({ close }) {

  // ================= DRAG =================
  const [position, setPosition] = useState({ x: 200, y: 100 });
  const [dragging, setDragging] = useState(false);
  const offset = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    setDragging(true);
    offset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  useEffect(() => {
    const move = (e) => {
      if (!dragging) return;
      setPosition({
        x: e.clientX - offset.current.x,
        y: e.clientY - offset.current.y
      });
    };
    const up = () => setDragging(false);

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [dragging]);

  // ================= RESIZE =================
  const [size, setSize] = useState({ width: 900, height: 400 });

  const handleResize = (e) => {
    setSize(prev => ({
      width: Math.max(500, prev.width + e.movementX),
      height: Math.max(300, prev.height + e.movementY)
    }));
  };

  // ================= DATA =================
  const [data, setData] = useState([]);
  const [duration, setDuration] = useState("5min");

  useEffect(() => {
    const interval = setInterval(() => {
      const point = {
        time: Date.now(),
        AIT_2902: Number(localStorage.getItem("AIT_2902")) || 0,
        pH_2901: Number(localStorage.getItem("pH_2901")) || 0,
        TT_2907: Number(localStorage.getItem("TT_2907")) || 0,
        AIT_2901: Number(localStorage.getItem("AIT_2901")) || 0
      };

      setData(prev => [...prev.slice(-2000), point]);

    }, 500);

    return () => clearInterval(interval);
  }, []);

  // ================= FILTER =================
  const filtered = data.filter(d =>
    d.time >= Date.now() - (DURATIONS[duration] * 1000 || 300000)
  );

  // ================= CANVAS DRAW =================
  const canvasRef = useRef();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, size.width, size.height);

    if (filtered.length < 2) return;

    const maxX = size.width - 60;
    const maxY = size.height - 40;

    const drawLine = (key, color, scale) => {
      ctx.beginPath();
      ctx.strokeStyle = color;

      filtered.forEach((p, i) => {
        const x = (i / filtered.length) * maxX + 50;
        const y = maxY - (p[key] / scale) * maxY + 10;

        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });

      ctx.stroke();
    };

    // axes
    ctx.strokeStyle = "#555";
    ctx.beginPath();
    ctx.moveTo(50, 10);
    ctx.lineTo(50, maxY);
    ctx.lineTo(maxX, maxY);
    ctx.stroke();

    // curves
    drawLine("AIT_2902", "red", 20);
    drawLine("pH_2901", "green", 14);
    drawLine("TT_2907", "orange", 200);
    drawLine("AIT_2901", "cyan", 20);

  }, [filtered, size]);

  // ================= ACTIONS =================
  const handlePrint = () => window.print();

  const handlePDF = () => {
    alert("PDF export can be integrated with jsPDF if needed");
  };

  const handleShare = () => {
    alert("Share feature can use Web Share API");
  };

  return (
    <div
      className="trend-chart-container"
      style={{ left: position.x, top: position.y, width: size.width }}
    >

      {/* HEADER */}
      <div className="trend-chart-header" onMouseDown={handleMouseDown}>
        <h3>NK Tech DCS Stripping Multivariable Trend</h3>
      </div>

      {/* CONTROLS */}
      <div className="trend-chart-controls">
        {Object.keys(DURATIONS).map(d => (
          <button
            key={d}
            onClick={() => setDuration(d)}
            className={duration === d ? "active" : ""}
          >
            {d}
          </button>
        ))}
      </div>

      {/* CANVAS */}
      <canvas
        ref={canvasRef}
        width={size.width}
        height={size.height}
        className="trend-chart-canvas"
      />

      {/* LEGEND */}
      <div className="trend-chart-legend">
        <span className="red">AIT_2902 (H2S)</span>
        <span className="green">pH_2901</span>
        <span className="orange">TT_2907</span>
        <span className="cyan">AIT_2901</span>
      </div>

      {/* FOOTER */}
      <div className="trend-chart-footer">
        <button onClick={handlePrint}>Print</button>
        <button onClick={handlePDF}>PDF</button>
        <button onClick={handleShare}>Share</button>
        <button onClick={close}>Close</button>
      </div>

      {/* RESIZER */}
      <div
        className="resize-handle"
        onMouseDown={(e) => {
          e.preventDefault();
          window.addEventListener("mousemove", handleResize);
          window.addEventListener("mouseup", () => {
            window.removeEventListener("mousemove", handleResize);
          }, { once: true });
        }}
      />

    </div>
  );
}

export default StrippingMultivariableTrendChart;